import { Link } from "react-router-dom";
import { ArrowIcon, PhoneIcon, SparkIcon } from "../components/Icons";
import { BOOKING_URL, GOOGLE_REVIEWS_URL, PHONE_DISPLAY, PHONE_HREF, reviews } from "../site";

const featuredServices = [
  { number: "01", title: "Cuts & styling", text: "Thoughtful cuts, polished blowouts, texture-aware styling, and special-occasion finishes.", path: "/services/" },
  { number: "02", title: "Color & care", text: "Single-process color, dimensional foils, custom lightening, conditioning, and keratin treatments.", path: "/services/" },
  { number: "03", title: "Weddings", text: "Hair and makeup for brides, wedding parties, and flower girls, at the salon or on location.", path: "/wedding/" },
  { number: "04", title: "Makeup & waxing", text: "Full-face and express makeup, bridal packages, and facial waxing for a finished look.", path: "/make-up/" },
] as const;

export function HomePage() {
  return (
    <>
      <section className="home-hero">
        <div className="home-hero__image" />
        <div className="home-hero__overlay" />
        <div className="container home-hero__content">
          <p className="eyebrow eyebrow--light">A family salon in Englishtown, New Jersey</p>
          <h1>Great hair.<br /><em>Better days.</em></h1>
          <p className="home-hero__lead">At Nadia’s, we want you to look and feel your best every day.</p>
          <div className="hero-actions">
            <a className="button button--light" href={BOOKING_URL} target="_blank" rel="noreferrer">Book your appointment <ArrowIcon /></a>
            <Link className="text-link text-link--light" to="/services/">Explore our services <ArrowIcon /></Link>
          </div>
        </div>
        <a className="hero-phone" href={PHONE_HREF}><PhoneIcon /><span>Questions or same-day availability?</span><strong>{PHONE_DISPLAY}</strong></a>
      </section>

      <section className="trust-strip" aria-label="Salon highlights">
        <div className="container trust-strip__grid">
          <div><span>01</span><p><strong>Free consultations</strong><small>Find the right service before you commit.</small></p></div>
          <div><span>02</span><p><strong>Online booking</strong><small>Choose a time through our booking partner.</small></p></div>
          <div><span>03</span><p><strong>Wedding travel</strong><small>On-location appointments are available.</small></p></div>
        </div>
      </section>

      <section className="section intro-section">
        <div className="container intro-grid">
          <div className="intro-copy">
            <p className="eyebrow">The Nadia’s experience</p>
            <h2>A look that feels like you on your best day.</h2>
            <p>We know how good it feels when your hair looks and moves the way you want it to. Our attentive team starts by listening, then brings the cut, color, treatment, or finishing details together with care.</p>
            <p>Come for the service. Stay for the easy conversation, warm welcome, and confidence that follows you out the door.</p>
            <Link className="text-link" to="/staff/">Meet our staff <ArrowIcon /></Link>
          </div>
          <div className="intro-visual">
            <img className="intro-visual__large" src="/images/salon-detail-1.jpg" alt="Nadia's Salon styling area" />
            <img className="intro-visual__small" src="/images/salon-detail-2.jpg" alt="A detail inside Nadia's Salon" />
            <div className="intro-note"><SparkIcon /><span>Personal attention,<br />never a rushed visit.</span></div>
          </div>
        </div>
      </section>

      <section className="section section--ink service-overview">
        <div className="container">
          <div className="section-heading section-heading--split">
            <div><p className="eyebrow eyebrow--light">What we do</p><h2>Everyday care.<br />Event-level polish.</h2></div>
            <p>From a clean-up cut to dimensional color and full bridal-party styling, every service begins with what will work for your hair, your routine, and your plans.</p>
          </div>
          <div className="featured-services">
            {featuredServices.map((service) => (
              <Link key={service.number} className="featured-service" to={service.path}>
                <span>{service.number}</span>
                <div><h3>{service.title}</h3><p>{service.text}</p></div>
                <ArrowIcon />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="wedding-feature">
        <div className="wedding-feature__image" />
        <div className="wedding-feature__copy">
          <p className="eyebrow eyebrow--light">Weddings & special occasions</p>
          <h2>You planned the day.<br />We’ll perfect the look.</h2>
          <p>Bridal hair, makeup, and party services can happen in the salon or travel to you. Parties of six or more receive champagne, orange juice, and hors d’oeuvres in the salon.</p>
          <Link className="button button--light" to="/wedding/">Plan your wedding services <ArrowIcon /></Link>
        </div>
      </section>

      <section className="section reviews-section">
        <div className="container">
          <div className="section-heading section-heading--center">
            <p className="eyebrow">In their words</p>
            <h2>Our clients leave feeling seen.</h2>
          </div>
          <div className="review-grid">
            {reviews.map((review, index) => (
              <figure key={review.name} className={index === 1 ? "review-card review-card--accent" : "review-card"}>
                <span className="quote-mark">“</span>
                <blockquote>{review.quote}</blockquote>
                <figcaption>
                  <strong>{review.name}</strong>
                  <span aria-label={`${review.rating} out of 5 stars`}>★★★★★</span>
                  <small>Google review</small>
                </figcaption>
              </figure>
            ))}
          </div>
          <a className="reviews-source text-link" href={GOOGLE_REVIEWS_URL} target="_blank" rel="noreferrer">
            See Nadia’s Salon on Google <ArrowIcon />
          </a>
        </div>
      </section>

      <section className="booking-banner">
        <div className="container booking-banner__inner">
          <div><p className="eyebrow eyebrow--light">Your chair is waiting</p><h2>Ready for your next look?</h2></div>
          <div className="booking-banner__actions">
            <a className="button button--light" href={BOOKING_URL} target="_blank" rel="noreferrer">Book online <ArrowIcon /></a>
            <a className="text-link text-link--light" href={PHONE_HREF}>Or call {PHONE_DISPLAY}</a>
          </div>
        </div>
      </section>
    </>
  );
}
